/**
 * Turns an axios failure into something a shop owner can act on.
 *
 * Every order screen used to funnel auth failures, permission failures,
 * gateway restarts, timeouts and dropped connections into the same
 * "Failed to load orders" toast, which is why the recurring network error
 * could never be traced: the status code never left the console. Each
 * failure now carries a distinct message and a short code that support can
 * quote back.
 */

export type ApiErrorKind =
  | "cancelled"
  | "offline"
  | "network"
  | "timeout"
  | "unauthorized"
  | "forbidden"
  | "not-found"
  | "rate-limited"
  | "server"
  | "unknown";

export interface DescribedApiError {
  kind: ApiErrorKind;
  /** Short trace code shown in the toast, e.g. "NETWORK" or "HTTP 503". */
  code: string;
  /** Full sentence shown to the user. */
  message: string;
  /** Whether trying the same request again has a realistic chance of working. */
  retryable: boolean;
  /** Cancelled requests are expected (navigation, filter change) — never toast them. */
  silent: boolean;
  status?: number;
  /** Automatic retries already spent by the axios interceptor. */
  attempts: number;
  /** Stable key for toast de-duplication while a poll loop keeps failing. */
  toastId: string;
}

/**
 * Covers every shape a cancellation arrives in without importing axios —
 * `__CANCEL__` is what `axios.isCancel` itself checks, and the code/name pair
 * covers axios v1's CanceledError and a bare AbortController abort.
 */
const isCancelled = (error: any): boolean =>
  error?.__CANCEL__ === true ||
  error?.code === "ERR_CANCELED" ||
  error?.name === "CanceledError" ||
  error?.name === "AbortError";

/** Server-supplied message, when it is a real sentence rather than a stack or an enum. */
const serverMessage = (error: any): string | undefined => {
  const raw = error?.response?.data?.message;
  const text = Array.isArray(raw) ? raw[0] : raw;
  if (typeof text !== "string") return undefined;
  const trimmed = text.trim();
  if (!trimmed || trimmed.length > 160) return undefined;
  return trimmed;
};

/**
 * @param error   the rejected axios error
 * @param subject what the user was trying to see, e.g. "orders" or "order history"
 */
export const describeApiError = (
  error: any,
  subject: string = "data"
): DescribedApiError => {
  const attempts: number = Number(error?.retryAttempts) || 0;
  const status: number | undefined = error?.response?.status;
  const fromServer = serverMessage(error);

  const build = (
    kind: ApiErrorKind,
    code: string,
    message: string,
    retryable: boolean,
    silent = false
  ): DescribedApiError => ({
    kind,
    code,
    message: silent ? message : `${message} (${code})`,
    retryable,
    silent,
    status,
    attempts,
    toastId: `api-error:${subject}:${kind}`,
  });

  if (isCancelled(error)) {
    return build("cancelled", "CANCELLED", "", false, true);
  }

  // No response at all — the request never reached the API.
  if (!error?.response) {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      return build(
        "offline",
        "OFFLINE",
        `This device is offline, so ${subject} could not be loaded. Reconnect and try again.`,
        true
      );
    }

    if (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT") {
      return build(
        "timeout",
        "TIMEOUT",
        `The server took too long to send ${subject}. Try again in a moment.`,
        true
      );
    }

    return build(
      "network",
      "NETWORK",
      `The server could not be reached, so ${subject} could not be loaded. Check the internet connection, then retry.`,
      true
    );
  }

  if (status === 401) {
    return build(
      "unauthorized",
      "HTTP 401",
      "This session has expired. Sign in again to continue.",
      false
    );
  }

  if (status === 403) {
    return build(
      "forbidden",
      "HTTP 403",
      fromServer || `This account does not have permission to view ${subject}.`,
      false
    );
  }

  if (status === 404) {
    return build(
      "not-found",
      "HTTP 404",
      fromServer || `${subject.charAt(0).toUpperCase()}${subject.slice(1)} could not be found. Refresh the page and try again.`,
      false
    );
  }

  if (status === 429) {
    return build(
      "rate-limited",
      "HTTP 429",
      `The server is busy. ${subject.charAt(0).toUpperCase()}${subject.slice(1)} will load again shortly.`,
      true
    );
  }

  if (status && status >= 500) {
    return build(
      "server",
      `HTTP ${status}`,
      `The server could not return ${subject}. ${
        attempts > 0 ? `Retried ${attempts} time${attempts === 1 ? "" : "s"}. ` : ""
      }Retry, or report this code to support.`,
      true
    );
  }

  return build(
    "unknown",
    status ? `HTTP ${status}` : "ERROR",
    fromServer || `${subject.charAt(0).toUpperCase()}${subject.slice(1)} could not be loaded.`,
    false
  );
};
